import * as React from 'react';
import {
    Button,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Slide,
    CircularProgress,
    useMediaQuery,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PrimaryButton, SecondaryButton } from "../SystemDesign/styles";
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import theme from '../theme';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FullScreenDialog = ({
    triggerButton,
    title,
    subtitle,
    content,
    existAction,
    actionTitle,
    actionFunction,
    onClose,
    isLoading,
    actionType,
    formId,
    disableAction,
    open: externalOpen,
    onExternalClose,
    children,
    showCloseButton = true,
    showActionInToolbar = true,
    closeIcon,
    toolbarProps,
    appBarProps,
    ...otherProps
}) => {
    const [open, setOpen] = React.useState(externalOpen || false);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = (event, reason) => {
        // Ignore backdrop clicks if needed
        if (reason && reason === "backdropClick") {
            return;
        }
        setOpen(false);
        // Call both onClose and onExternalClose handlers if provided
        if (onClose && typeof onClose === 'function') {
            onClose();
        }
        if (onExternalClose) {
            onExternalClose();
        }
    };

    const handleAction = () => {
        if (actionFunction) {
            actionFunction();
        }
    };

    // Removed problematic useEffect that was closing dialog when onClose prop changed
    
    // Sync with external open state if provided
    useEffect(() => {
        if (externalOpen !== undefined) {
            setOpen(externalOpen);
        }
    }, [externalOpen]);

    return (
        <React.Fragment>
            {triggerButton && React.cloneElement(triggerButton, { onClick: handleClickOpen })}
            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                slots={{
                    transition: Transition,
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        overflow: 'hidden !important'
                    }
                }}
                {...otherProps}
            >
                <AppBar 
                    sx={{ 
                        position: 'relative',
                        backgroundColor: theme.colors.primary[500],
                        '&:hover': {
                            backgroundColor: theme.colors.primary[600]
                        }
                    }}
                    {...appBarProps}
                >
                    <Toolbar {...toolbarProps}>
                        {showCloseButton && (
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                {closeIcon || <CloseIcon />}
                            </IconButton>
                        )}
                        
                        <div style={{ marginLeft: showCloseButton ? (isMobile ? 8 : 16) : 0, flex: 1 }}>
                            {title && (
                                <Typography 
                                    variant={isMobile ? "subtitle1" : "h6"} 
                                    component="div"
                                    sx={{ 
                                        color: 'white', 
                                        fontWeight: 600,
                                        fontSize: isMobile ? '1rem' : '1.25rem',
                                        lineHeight: isMobile ? 1.2 : 1.6
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                            {subtitle && (
                                <Typography 
                                    variant={isMobile ? "caption" : "body2"} 
                                    sx={{ 
                                        color: 'rgba(255, 255, 255, 0.8)', 
                                        fontSize: isMobile ? '0.75rem' : '14px',
                                        mt: isMobile ? 0.25 : 0.5,
                                        lineHeight: isMobile ? 1.1 : 1.4,
                                        display: isMobile ? '-webkit-box' : 'block',
                                        WebkitLineClamp: isMobile ? 2 : 'none',
                                        WebkitBoxOrient: isMobile ? 'vertical' : 'initial',
                                        overflow: isMobile ? 'hidden' : 'visible'
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </div>

                        {existAction && showActionInToolbar && (
                            <Button
                                autoFocus
                                color="inherit"
                                onClick={handleAction}
                                type={actionType}
                                form={formId}
                                disabled={isLoading || disableAction}
                                sx={{
                                    backgroundColor: theme.colors.secondary[300],
                                    color: theme.colors.primary[900],
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '6px 12px' : '8px 16px',
                                    minWidth: isMobile ? 'auto' : '64px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.secondary[400]
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'rgba(255, 255, 255, 0.5)'
                                    }
                                }}
                            >
                                {isLoading ? <CircularProgress size={isMobile ? 16 : 20} color="inherit" /> : actionTitle}
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>

                {children ? (
                    // New approach: render children directly
                    children
                ) : (
                    // Original approach: render using content prop
                    <div style={{
                        flex: 1,
                        backgroundColor: theme.colors.secondary[50]
                    }}>
                        {content}
                        
                        {/* Bottom action bar for actions not in toolbar */}
                        {existAction && !showActionInToolbar && (
                            <div style={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: '16px',
                                backgroundColor: 'white',
                                borderTop: `1px solid ${theme.colors.primary[200]}`,
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end'
                            }}>
                                <Button 
                                    onClick={handleClose} 
                                    sx={SecondaryButton}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    onClick={handleAction}
                                    sx={PrimaryButton}
                                    type={actionType}
                                    form={formId}
                                    disabled={isLoading || disableAction}
                                >
                                    {isLoading ? <CircularProgress size={24} /> : actionTitle}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </React.Fragment>
    );
};

FullScreenDialog.propTypes = {
    triggerButton: PropTypes.element,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    content: PropTypes.node,
    existAction: PropTypes.bool,
    actionTitle: PropTypes.string,
    actionFunction: PropTypes.func,
    onClose: PropTypes.func,
    onExternalClose: PropTypes.func,
    actionType: PropTypes.string,
    formId: PropTypes.string,
    disableAction: PropTypes.bool,
    open: PropTypes.bool,
    isLoading: PropTypes.bool,
    children: PropTypes.node,
    showCloseButton: PropTypes.bool,
    showActionInToolbar: PropTypes.bool,
    closeIcon: PropTypes.element,
    toolbarProps: PropTypes.object,
    appBarProps: PropTypes.object
};

export default FullScreenDialog;